import * as React from "react"
import { cn } from "../../lib/utils"

interface DropdownMenuProps {
  children: React.ReactNode
}

interface DropdownMenuTriggerProps {
  asChild?: boolean
  children: React.ReactNode
}

interface DropdownMenuContentProps {
  className?: string
  align?: "start" | "center" | "end"
  forceMount?: boolean
  children: React.ReactNode
}

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface DropdownMenuLabelProps {
  className?: string
  children: React.ReactNode
}

const DropdownMenu = ({ children }: DropdownMenuProps) => {
  const [open, setOpen] = React.useState(false)
  
  return (
    <div className="relative inline-block text-left">
      {React.Children.map(children, child => 
        React.isValidElement(child) 
          ? React.cloneElement(child, { open, setOpen } as Record<string, unknown>)
          : child
      )}
    </div>
  )
}

const DropdownMenuTrigger = ({ asChild, children, ...props }: DropdownMenuTriggerProps & Record<string, unknown>) => {
  const handleClick = () => {
    props.setOpen?.(!props.open)
  }
  
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, { onClick: handleClick })
  }
  
  return (
    <button onClick={handleClick}>
      {children}
    </button>
  )
}

const DropdownMenuContent = ({ className, align = "start", children, ...props }: DropdownMenuContentProps & Record<string, unknown>) => {
  if (!props.open) return null
  
  const alignClasses = {
    start: "left-0",
    center: "left-1/2 -translate-x-1/2", 
    end: "right-0"
  }
  
  return (
    <div className={cn(
      "absolute z-50 mt-2 min-w-[8rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-lg",
      alignClasses[align],
      className
    )}>
      {children}
    </div>
  )
}

const DropdownMenuItem = ({ className, children, onClick, ...props }: DropdownMenuItemProps) => {
  return (
    <div
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  )
}

const DropdownMenuLabel = ({ className, children }: DropdownMenuLabelProps) => {
  return (
    <div className={cn("px-2 py-1.5 text-sm font-semibold", className)}>
      {children}
    </div>
  )
}

const DropdownMenuSeparator = () => {
  return <div className="-mx-1 my-1 h-px bg-muted" />
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
}